import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

const LOGO_URL = 'https://static.readdy.ai/image/358c420565fea72b91269b0160885e8f/345a23f26c025716610c41033cbf85f6.png';
const WA_LINK = 'https://wa.me/5521990212845?text=Ol%C3%A1,%20vi%20o%20site%20do%20M%C3%A9todo%20Equilibradamente%20e%20gostaria%20de%20saber%20mais%20sobre%20a%20sess%C3%A3o%20gratuita.';

export default function HeroSection() {
  const { ref, visible } = useScrollAnimation(0.05);

  return (
    <section
      className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden pt-20"
      style={{ background: 'linear-gradient(160deg, #060D18 0%, #0A1628 50%, #060D18 100%)' }}
    >
      {/* Background glow shapes */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          className="absolute -top-40 left-1/2 -translate-x-1/2 w-[700px] h-[500px] rounded-full"
          style={{ background: 'radial-gradient(ellipse, rgba(0,200,232,0.08) 0%, transparent 70%)' }}
        />
        <div
          className="absolute bottom-0 right-0 w-[400px] h-[400px] rounded-full"
          style={{ background: 'radial-gradient(circle, rgba(245,165,32,0.06) 0%, transparent 70%)' }}
        />
        <div
          className="absolute top-1/3 -left-20 w-[300px] h-[300px] rounded-full"
          style={{ background: 'radial-gradient(circle, rgba(61,204,120,0.05) 0%, transparent 70%)' }}
        />
      </div>

      <div
        ref={ref}
        className="relative z-10 max-w-3xl mx-auto text-center px-6 py-12 md:py-16"
        style={{
          opacity: visible ? 1 : 0,
          transform: visible ? 'translateY(0)' : 'translateY(32px)',
          transition: 'opacity 0.8s ease, transform 0.8s ease',
        }}
      >
        {/* Logo */}
        <div className="flex justify-center mb-6">
          <img
            src={LOGO_URL}
            alt="EquilibradaMente™"
            className="w-24 h-24 object-contain"
          />
        </div>

        {/* Label */}
        <span
          className="inline-block text-xs font-semibold tracking-widest uppercase px-4 py-2 rounded-full mb-7 border"
          style={{
            background: 'rgba(0,200,232,0.08)',
            color: '#00C8E8',
            borderColor: 'rgba(0,200,232,0.2)',
            letterSpacing: '0.15em',
          }}
        >
          SESSÃO INICIAL GRATUITA
        </span>

        {/* Headline */}
        <h1 className="text-4xl md:text-5xl font-semibold leading-tight mb-6" style={{ color: '#FFFFFF' }}>
          Regulação do sistema nervoso
          <br />
          e reprogramação emocional online
        </h1>

        {/* Identification phrase */}
        <p className="text-base md:text-lg font-light mb-4 max-w-xl mx-auto leading-relaxed" style={{ color: '#E0EEF7', letterSpacing: '0.01em' }}>
          Sua mente não para? Pensamentos acelerados podem tirar sua paz. Existe um caminho para reequilibrar sua mente.
        </p>

        {/* Subheadline */}
        <p className="text-base md:text-lg font-light mb-8 max-w-xl mx-auto leading-relaxed" style={{ color: '#C8DCEA', letterSpacing: '0.01em' }}>
          Uma experiência guiada para desacelerar a mente e recuperar equilíbrio emocional.
        </p>

        {/* Micro emotional script */}
        <div
          className="rounded-2xl px-8 py-6 mb-10 text-left max-w-xl mx-auto border"
          style={{ background: 'rgba(15,30,48,0.8)', borderColor: 'rgba(0,200,232,0.15)' }}
        >
          <p className="text-base leading-relaxed italic font-light" style={{ color: '#B8D4E8' }}>
            Talvez sua mente esteja cansada de ficar em alerta o tempo todo.<br />
            Talvez você só queira sentir um pouco mais de silêncio por dentro.<br />
            <span className="font-medium not-italic" style={{ color: '#00C8E8' }}>
              Essa sessão foi criada exatamente para esse momento.
            </span>
          </p>
        </div>

        {/* Primary CTA */}
        <a
          href={WA_LINK}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-2 whitespace-nowrap font-semibold rounded-full px-10 py-4 text-base mb-4 cursor-pointer"
          style={{
            background: 'linear-gradient(135deg, #25D366, #1DB954)',
            color: '#FFFFFF',
            transition: 'transform 0.2s, box-shadow 0.2s',
          }}
          onMouseEnter={e => {
            (e.currentTarget as HTMLAnchorElement).style.transform = 'translateY(-2px)';
            (e.currentTarget as HTMLAnchorElement).style.boxShadow = '0 8px 30px rgba(37,211,102,0.45)';
          }}
          onMouseLeave={e => {
            (e.currentTarget as HTMLAnchorElement).style.transform = 'translateY(0)';
            (e.currentTarget as HTMLAnchorElement).style.boxShadow = 'none';
          }}
        >
          <span className="w-5 h-5 flex items-center justify-center">
            <i className="ri-whatsapp-line text-base" />
          </span>
          Falar no WhatsApp
        </a>

        {/* Microcopy */}
        <p className="text-sm mb-10" style={{ color: '#5A7A95' }}>
          Sessão gratuita &nbsp;•&nbsp; Sem cartão &nbsp;•&nbsp; Duração aproximada: 15 min
        </p>

        {/* Trust indicators */}
        <div className="flex flex-wrap items-center justify-center gap-6">
          {[
            { icon: 'ri-lock-line', label: 'Ambiente confidencial' },
            { icon: 'ri-brain-line', label: 'Conteúdo psicoeducativo' },
            { icon: 'ri-leaf-line', label: 'Experiência guiada' },
          ].map(item => (
            <div key={item.label} className="flex items-center gap-2">
              <span className="w-5 h-5 flex items-center justify-center" style={{ color: '#00C8E8' }}>
                <i className={`${item.icon} text-base`} />
              </span>
              <span className="text-sm font-medium" style={{ color: '#7A9BB5' }}>{item.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Scroll hint */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 opacity-40">
        <span className="text-xs tracking-widest uppercase" style={{ color: '#00C8E8' }}>rolar</span>
        <i className="ri-arrow-down-line text-xl" style={{ color: '#00C8E8' }} />
      </div>
    </section>
  );
}